import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Eye, Users, TrendingUp, Globe, ArrowUp,
  Loader2, BarChart3, Link2, FileText,
} from "lucide-react";

const API = "/api";

const PAGE_LABELS = {
  "/": "Home",
  "/residenziale": "Residenziale",
  "/pmi": "PMI",
  "/blog": "Blog",
  "/prodotti": "Prodotti",
  "/privacy": "Privacy",
  "/configuratore": "Configuratore",
};

function formatPath(path) {
  if (PAGE_LABELS[path]) return PAGE_LABELS[path];
  if (path.startsWith("/promo/")) return `Landing: ${path.replace("/promo/", "")}`;
  if (path.startsWith("/blog/")) return `Articolo: ${path.replace("/blog/", "")}`;
  if (path.startsWith("/prodotti/")) return `Prodotto: ${path.replace("/prodotti/", "")}`;
  return path;
}

const SOURCE_LABELS = {
  wizard: "Sito Diretto (Wizard)",
  landing_promo: "Landing Promozionale",
};

function MiniBar({ value, max, color = "bg-emerald-500" }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="h-2 bg-slate-100 rounded-full overflow-hidden flex-1">
      <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const res = await axios.get(`${API}/analytics/overview`);
        setData(res.data);
      } catch {
        toast.error("Errore nel caricamento analytics");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="analytics-loading">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!data) return null;

  const { views, visitors, top_pages, daily_trend, leads, top_referrers } = data;
  const maxViews = top_pages.length > 0 ? top_pages[0].views : 1;
  const maxLeadSource = leads.by_source.length > 0 ? Math.max(...leads.by_source.map((s) => s.count)) : 1;

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard
          icon={Eye} label="Visite Totali"
          value={views.total} sub={`${views.last_7d} ultimi 7gg`}
          color="bg-blue-50 text-blue-600"
        />
        <SummaryCard
          icon={Users} label="Visitatori Unici"
          value={visitors.total} sub={`${visitors.last_7d} ultimi 7gg`}
          color="bg-emerald-50 text-emerald-600"
        />
        <SummaryCard
          icon={TrendingUp} label="Lead Totali"
          value={leads.total}
          sub={`${leads.by_source.length} fonti`}
          color="bg-amber-50 text-amber-600"
        />
        <SummaryCard
          icon={Globe} label="Pagine Visitate"
          value={top_pages.length}
          sub={`Top: ${top_pages.length > 0 ? formatPath(top_pages[0].path) : "-"}`}
          color="bg-purple-50 text-purple-600"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Trend */}
        <div className="bg-white rounded-xl border border-slate-100 p-5" data-testid="analytics-daily-trend">
          <h3 className="text-sm font-bold text-slate-900 mb-4 flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            <BarChart3 className="w-4 h-4 text-blue-500" /> Visite Giornaliere (30 giorni)
          </h3>
          {daily_trend.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-8">Nessun dato disponibile</p>
          ) : (
            <div className="flex items-end gap-[2px] h-36">
              {daily_trend.map((day) => {
                const maxDay = Math.max(...daily_trend.map((d) => d.views), 1);
                const heightPct = (day.views / maxDay) * 100;
                return (
                  <div key={day.date} className="flex-1 flex flex-col items-center justify-end group relative">
                    <div className="absolute -top-8 bg-slate-800 text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                      {day.date}: {day.views} visite, {day.unique} unici
                    </div>
                    <div
                      className="w-full bg-emerald-400 hover:bg-emerald-500 rounded-t transition-colors min-h-[2px]"
                      style={{ height: `${Math.max(heightPct, 2)}%` }}
                    />
                  </div>
                );
              })}
            </div>
          )}
          {daily_trend.length > 0 && (
            <div className="flex justify-between mt-2 text-[10px] text-slate-400">
              <span>{daily_trend[0]?.date}</span>
              <span>{daily_trend[daily_trend.length - 1]?.date}</span>
            </div>
          )}
        </div>

        {/* Lead Sources */}
        <div className="bg-white rounded-xl border border-slate-100 p-5" data-testid="analytics-lead-sources">
          <h3 className="text-sm font-bold text-slate-900 mb-4 flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            <TrendingUp className="w-4 h-4 text-amber-500" /> Lead per Fonte
          </h3>
          {leads.by_source.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-8">Nessun lead registrato</p>
          ) : (
            <div className="space-y-3">
              {leads.by_source.map((src) => (
                <div key={src.source} className="flex items-center gap-3" data-testid={`lead-source-${src.source}`}>
                  <span className="text-xs text-slate-600 w-44 truncate">{SOURCE_LABELS[src.source] || src.source}</span>
                  <MiniBar value={src.count} max={maxLeadSource} color="bg-amber-400" />
                  <span className="text-sm font-bold text-slate-900 w-10 text-right">{src.count}</span>
                </div>
              ))}
            </div>
          )}

          {leads.by_landing.length > 0 && (
            <>
              <h4 className="text-xs font-semibold text-slate-500 mt-6 mb-3 uppercase tracking-wide">Dettaglio Landing</h4>
              <div className="space-y-2">
                {leads.by_landing.map((lp) => (
                  <div key={lp.landing} className="flex items-center gap-3">
                    <span className="text-xs text-slate-500 w-44 truncate">{lp.landing}</span>
                    <MiniBar value={lp.count} max={leads.by_landing[0]?.count || 1} color="bg-purple-400" />
                    <span className="text-xs font-semibold text-slate-700 w-10 text-right">{lp.count}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Pages */}
        <div className="bg-white rounded-xl border border-slate-100 p-5" data-testid="analytics-top-pages">
          <h3 className="text-sm font-bold text-slate-900 mb-4 flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            <FileText className="w-4 h-4 text-emerald-500" /> Pagine Piu Viste
          </h3>
          {top_pages.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-8">Nessuna visita registrata</p>
          ) : (
            <div className="space-y-2.5">
              {top_pages.map((page, i) => (
                <div key={page.path} className="flex items-center gap-3" data-testid={`top-page-${i}`}>
                  <span className="text-xs font-medium text-slate-400 w-5">{i + 1}.</span>
                  <span className="text-xs text-slate-700 w-40 truncate">{formatPath(page.path)}</span>
                  <MiniBar value={page.views} max={maxViews} />
                  <div className="flex items-center gap-2 w-24 justify-end">
                    <span className="text-xs text-slate-500">{page.unique_visitors} <Users className="w-3 h-3 inline" /></span>
                    <span className="text-sm font-bold text-slate-900">{page.views}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top Referrers */}
        <div className="bg-white rounded-xl border border-slate-100 p-5" data-testid="analytics-referrers">
          <h3 className="text-sm font-bold text-slate-900 mb-4 flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            <Link2 className="w-4 h-4 text-blue-500" /> Principali Referrer
          </h3>
          {top_referrers.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-8">Nessun referrer registrato</p>
          ) : (
            <div className="space-y-2.5">
              {top_referrers.map((ref, i) => {
                let domain = ref.referrer;
                try { domain = new URL(ref.referrer).hostname; } catch {}
                return (
                  <div key={ref.referrer} className="flex items-center gap-3" data-testid={`referrer-${i}`}>
                    <span className="text-xs font-medium text-slate-400 w-5">{i + 1}.</span>
                    <span className="text-xs text-slate-700 flex-1 truncate">{domain}</span>
                    <span className="text-sm font-bold text-slate-900">{ref.count}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="bg-white rounded-xl border border-slate-100 p-5 hover:shadow-md transition-shadow" data-testid={`analytics-card-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center`}>
          <Icon className="w-5 h-5" strokeWidth={1.5} />
        </div>
        {value > 0 && <ArrowUp className="w-4 h-4 text-emerald-500" />}
      </div>
      <div className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{value}</div>
      <div className="text-xs text-slate-500 mt-0.5">{sub || label}</div>
    </div>
  );
}
